import React, { FC } from 'react';
import { Form, Row } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import PropTypes from 'prop-types';

import { getUserGroupsLabel, getUsersLabel, MultiSelect } from '@spidertracks/components';
import { RecipientSelectorProps } from './types';
import { getMembersNotInGroupsForRecipients } from './getMembersNotInGroupsForRecipients';
import { getGroupsAndMembersFromRecipients } from './getGroupsAndMembersFromRecipients';
import { getSelectedGroupNamesForLabels } from './getSelectedGroupNamesForLabels';

export const RecipientSelector: FC<RecipientSelectorProps> = ({
  form,
  users,
  groups,
  required = true
}) => {
  users = users ?? [];
  groups = groups ?? [];

  const selectedRecipients: string[] = form.getFieldValue('users') ?? [];
  const {
    selectedGroupIds,
    selectedMemberIds,
    groupsOptions,
    membersOptions
  } = getGroupsAndMembersFromRecipients(selectedRecipients, groups, users);

  const selectedGroupNames = getSelectedGroupNamesForLabels(selectedGroupIds, groups);
  const selectedMemberNames = users
    ?.filter(user => selectedMemberIds.includes(user.id))
    .map(user => user.name);

  const onSelectGroupsHandler = (submittedGroupIds: CheckboxValueType[]) => {
    const allUsersGroupId = groups.find(group => group.type === 'USERS')?.id;
    const removedMembersInGroups = getMembersNotInGroupsForRecipients(
      selectedMemberIds,
      submittedGroupIds as string[],
      groups
    );
    const processedGroupIds =
      allUsersGroupId && submittedGroupIds.includes(allUsersGroupId)
        ? [allUsersGroupId]
        : submittedGroupIds;
    const joinMembersAndGroups = processedGroupIds.concat(removedMembersInGroups);
    form.setFieldsValue({ users: joinMembersAndGroups });
  };

  const onSelectMembersHandler = (submittedUserIds: CheckboxValueType[]) => {
    const removedMembersInGroups = getMembersNotInGroupsForRecipients(
      submittedUserIds as string[],
      selectedGroupIds,
      groups
    );
    const joinMembersAndGroups = selectedGroupIds.concat(removedMembersInGroups);
    form.setFieldsValue({ users: joinMembersAndGroups });
  };

  return (
    <Form.Item label={'Recipients'} style={{ textAlign: 'left' }} className="mt-3">
      <Row>
        {/* AntDesign does not allow sharing multiple dropdowns with the same field, override with 'trigger' below */}
        {form.getFieldDecorator('users', {
          initialValue: [],
          trigger: '', // Note: prevent two-way binding on the div below
          rules: [
            {
              required: required,
              message: 'Please select at least a recipient'
            }
          ]
        })(
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <MultiSelect
              closedLabel={getUserGroupsLabel(
                selectedGroupNames,
                required ? 'No groups selected' : 'No groups selected (optional)'
              )}
              displayName={'groups'}
              onSelect={onSelectGroupsHandler}
              selected={selectedGroupIds}
              options={groupsOptions}
            />
            <MultiSelect
              closedLabel={getUsersLabel(
                selectedMemberNames,
                required ? 'No members selected' : 'No members selected (optional)'
              )}
              displayName={'users'}
              showAll={true}
              onSelect={onSelectMembersHandler}
              selected={selectedMemberIds}
              options={membersOptions}
            />
          </div>
        )}
      </Row>
    </Form.Item>
  );
};

export default RecipientSelector;

RecipientSelector.propTypes = {
  form: PropTypes.any,
  users: PropTypes.array.isRequired,
  groups: PropTypes.array.isRequired,
  required: PropTypes.bool
};
